import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useMemo, useState } from "react";

const readStoredStringArray = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export interface UsePersistedMultiSelectFilterOptions {
  storageKey: string;
  validOptions?: readonly string[];
}

export const usePersistedMultiSelectFilter = ({ storageKey, validOptions }: UsePersistedMultiSelectFilterOptions) => {
  const [selected, setSelected] = useState<string[]>(() => readStoredStringArray(storageKey));

  const validSelected = useMemo(() => {
    if (!validOptions) {
      return selected;
    }
    return selected.filter((value) => validOptions.includes(value));
  }, [selected, validOptions]);

  const update = (values: string[]) => {
    setSelected(values);
    if (values.length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(values));
    }
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    update(typeof value === "string" ? value.split(",") : value);
  };

  const clear = () => update([]);

  return {
    clear,
    handleChange,
    selected,
    update,
    validSelected,
  };
};

export interface TableNameTypeFiltersProps {
  nameFilterId?: string;
  nameOptions: string[];
  namesStorageKey: string;
  onClear: () => void;
  onNamesChange: (event: SelectChangeEvent<string[]>) => void;
  onTypesChange?: (event: SelectChangeEvent<string[]>) => void;
  selectedNames: string[];
  selectedTypes?: string[];
  showTypeFilter?: boolean;
  typeFilterId?: string;
  typeOptions?: readonly string[];
}

export const TableNameTypeFilters = ({
  nameFilterId = "table-name-filter",
  nameOptions,
  onClear,
  onNamesChange,
  onTypesChange,
  selectedNames,
  selectedTypes = [],
  showTypeFilter = false,
  typeFilterId = "table-type-filter",
  typeOptions = [],
}: TableNameTypeFiltersProps) => {
  const hasActiveFilters = selectedNames.length > 0 || selectedTypes.length > 0;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id={`${nameFilterId}-label`}>Filter by Name</InputLabel>
        <Select
          id={nameFilterId}
          input={<OutlinedInput label="Filter by Name" />}
          labelId={`${nameFilterId}-label`}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
          multiple
          onChange={onNamesChange}
          renderValue={(selected) => (selected.length === 0 ? "" : `${selected.length} selected`)}
          value={selectedNames}
        >
          {nameOptions.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox checked={selectedNames.includes(name)} size="small" />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showTypeFilter && onTypesChange ? (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`${typeFilterId}-label`}>Filter by Type</InputLabel>
          <Select
            id={typeFilterId}
            input={<OutlinedInput label="Filter by Type" />}
            labelId={`${typeFilterId}-label`}
            multiple
            onChange={onTypesChange}
            renderValue={(selected) => (selected.length === 0 ? "" : `${selected.length} selected`)}
            value={selectedTypes}
          >
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={selectedTypes.includes(type)} size="small" />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      {hasActiveFilters ? (
        <Box sx={{ alignItems: "center", display: "flex" }}>
          <Chip label="Clear filters" onDelete={onClear} size="small" variant="outlined" />
        </Box>
      ) : null}
    </Box>
  );
};

export const applyNameFilter = <T extends { name: string }>(
  rows: T[],
  selectedNames: string[],
  limit: number | "all",
): T[] => {
  const filtered = selectedNames.length > 0 ? rows.filter((row) => selectedNames.includes(row.name)) : rows;

  if (selectedNames.length > 0 || limit === "all") {
    return filtered;
  }

  return filtered.slice(0, limit);
};
